import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import {
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Flame,
  Calendar,
  Tag,
  BookOpen,
  User,
  Sparkles,
  Sunrise,
  Target,
  ArrowRight,
} from "lucide-react";
import { InsightStats as InsightStatsType } from "./types";
import { cn } from "../../../../lib/utils";

interface InsightStatsProps {
  stats: InsightStatsType;
  onViewCategory: (categoryId: string) => void;
  onViewSource: (source: string) => void;
}

const InsightStats: React.FC<InsightStatsProps> = ({
  stats,
  onViewCategory,
  onViewSource,
}) => {
  const { t } = useTranslation();

  const monthChange = stats.thisMonth - stats.lastMonth;
  const monthChangePercent = stats.lastMonth
    ? Math.round((monthChange / stats.lastMonth) * 100)
    : 100;

  const getSourceIcon = (source: string) => {
    switch (source) {
      case "engagement":
        return <BookOpen className="h-4 w-4" />;
      case "mentor_assigned":
        return <User className="h-4 w-4" />;
      case "sketchbook":
        return <Sparkles className="h-4 w-4" />;
      case "reflection":
        return <Sunrise className="h-4 w-4" />;
      default:
        return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case "engagement":
        return t("from_engagements") || "From Engagements";
      case "mentor_assigned":
        return t("from_mentor") || "From Mentor";
      case "sketchbook":
        return t("ai_generated") || "AI Generated";
      case "reflection":
        return t("source_reflection") || "Daily reflection";
      default:
        return t("personal") || "Personal";
    }
  };

  // Calculate max for bar charts
  const maxCategoryCount = Math.max(...stats.byCategory.map((c) => c.count), 1);
  const maxSourceCount = Math.max(...stats.bySource.map((s) => s.count), 1);
  const maxMonthCount = Math.max(...stats.byMonth.map((m) => m.count), 1);

  return (
    <div className="space-y-6 pb-4">
      {/* Top Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Insights */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-100">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Lightbulb className="h-5 w-5 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-muted-foreground">
            {t("total_insights") || "Total Insights"}
          </p>
        </div>

        {/* This Month */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/8/50 border border-primary/10">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            {monthChange !== 0 && (
              <Badge
                variant="secondary"
                className={cn(
                  "text-xs",
                  monthChange > 0
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                )}
              >
                {monthChange > 0 ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 mr-1" />
                )}
                {monthChange > 0 ? "+" : ""}
                {monthChangePercent}%
              </Badge>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.thisMonth}</p>
          <p className="text-sm text-muted-foreground">
            {t("this_month") || "This Month"}
          </p>
        </div>

        {/* Current Streak */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-100">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Flame className="h-5 w-5 text-orange-600" />
            </div>
            {stats.streak.current >= 7 && (
              <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                🔥 {t("on_fire") || "On Fire!"}
              </Badge>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats.streak.current} {t("days") || "days"}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("current_streak") || "Current Streak"}
          </p>
        </div>

        {/* Categories Used */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Tag className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.byCategory.length}</p>
          <p className="text-sm text-muted-foreground">
            {t("categories_used") || "Categories Used"}
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* By Category */}
        <div className="p-5 rounded-xl border bg-white/50">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Tag className="h-4 w-4 text-gray-400" />
            {t("insights_by_category") || "Insights by Category"}
          </h3>
          {stats.byCategory.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {t("no_categories_yet") || "No categories yet"}
            </p>
          ) : (
            <div className="space-y-3">
              {stats.byCategory.slice(0, 5).map((cat) => (
                <button
                  key={cat.categoryId}
                  onClick={() => onViewCategory(cat.categoryId)}
                  className="w-full group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: cat.categoryColor }}
                      />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {cat.categoryName}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {cat.count}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all group-hover:opacity-80"
                      style={{
                        width: `${(cat.count / maxCategoryCount) * 100}%`,
                        backgroundColor: cat.categoryColor,
                      }}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* By Source */}
        <div className="p-5 rounded-xl border bg-white/50">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-gray-400" />
            {t("insights_by_source") || "Insights by Source"}
          </h3>
          {stats.bySource.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {t("no_sources_yet") || "No insights yet"}
            </p>
          ) : (
            <div className="space-y-3">
              {stats.bySource.map((source) => (
                <button
                  key={source.source}
                  onClick={() => onViewSource(source.source)}
                  className="w-full group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-gray-600 group-hover:text-gray-900">
                      {getSourceIcon(source.source)}
                      <span className="text-sm">{getSourceLabel(source.source)}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {source.count}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all group-hover:bg-primary"
                      style={{
                        width: `${(source.count / maxSourceCount) * 100}%`,
                      }}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Activity */}
      <div className="p-5 rounded-xl border bg-white/50">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          {t("monthly_activity") || "Monthly Activity"}
        </h3>
        <div className="flex items-end justify-between gap-1 h-32">
          {stats.byMonth.map((month, index) => (
            <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
              <div
                className={cn(
                  "w-full rounded-t transition-all hover:opacity-80",
                  index === stats.byMonth.length - 1
                    ? "bg-amber-500"
                    : "bg-amber-200"
                )}
                style={{
                  height: `${Math.max((month.count / maxMonthCount) * 100, 4)}%`,
                }}
              />
              <span className="text-xs text-muted-foreground">
                {month.month.slice(0, 3)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Streak Info & Tips */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Streak Details */}
        <div className="p-5 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100">
          <div className="flex items-center gap-3 mb-3">
            <Flame className="h-6 w-6 text-orange-500" />
            <div>
              <h3 className="font-semibold text-gray-900">
                {t("insight_streak") || "Insight Streak"}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t("streak_description") || "Days in a row with at least one insight"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-3xl font-bold text-orange-600">
                {stats.streak.current}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("current") || "Current"}
              </p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-400">
                {stats.streak.longest}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("longest") || "Longest"}
              </p>
            </div>
          </div>
        </div>

        {/* Weekly Goal */}
        <div className="p-5 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100">
          <div className="flex items-center gap-3 mb-3">
            <Target className="h-6 w-6 text-green-500" />
            <div>
              <h3 className="font-semibold text-gray-900">
                {t("weekly_goal") || "Weekly Goal"}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t("aim_for_insights") || "Aim for 5 insights per week"}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">
                {Math.min(stats.thisMonth, 5)}/5 {t("this_week") || "this week"}
              </span>
              <span className="text-sm font-medium text-green-600">
                {Math.round((Math.min(stats.thisMonth, 5) / 5) * 100)}%
              </span>
            </div>
            <div className="h-3 bg-green-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{
                  width: `${Math.min((stats.thisMonth / 5) * 100, 100)}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightStats;
